import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';
import { authenticatedApi, baseUrl, communityUrl } from 'api';
// Popup = Popover
// Header = Heading
// Placeholder = Skeleton

const objectTypes = {
    contact: {
        endpoint: 'contacts',
        singularLabel: 'Contact',
        pluralLabel: 'Contacts',
        fields: {
            primary: 'name',
            photo: 'profile_image',
            additional: {
                do_not_contact: {
                    label: 'Communications',
                    transformer: (object) => {
                        return 'DO NOT CONTACT';
                    }
                },
                email: 'Email',
                preferred_contact_method: 'Preferred Contact Method',
                cell_phone: 'Cell Phone',
                work_phone: 'Work Phone',
                home_phone: 'Home Phone',
            },
        }
    },
    workplan: {
        endpoint: 'work-plans',
        singularLabel: 'Work plan',
        pluralLabel: 'Work plans',
        fields: {
            primary: 'name',
            photo: null,
            additional: {
                description: 'Description',
                number_tasks: '# of Tasks',
                number_incomplete_tasks: '# of Remaining Tasks',
                workplan_date: {
                    label: 'Date',
                    transformer: (object) => {
                        const dateSplit = object.workplan_date.split('-');

                        return `${dateSplit[1]}/${dateSplit[2]}/${dateSplit[0]}`; // todo: parse into date format
                    }
                }
            },
        }
    },
    business: {
        endpoint: 'businesses',
        singularLabel: 'Business',
        pluralLabel: 'Businesses',
        fields: {
            primary: 'name',
            photo: null,
            additional: {
                do_not_contact: {
                    label: 'Communications',
                    transformer: (object) => {
                        return 'DO NOT CONTACT';
                    }
                },
                dba_name: 'DBA',
                description: 'Description',
                phone: 'Phone',
                url: {
                    label: 'Website',
                    transformer: (object) => {
                        return (<a href={object.url} target="_blank" className="clickable">Visit Website</a>)
                    }
                },
            },
        }
    },
    property: {
        endpoint: 'properties',
        singularLabel: 'Property',
        pluralLabel: 'Properties',
        fields: {
            primary: 'address',
            photo: null,
            additional: {
                owner: {
                    label: 'Owner',
                    transformer: (object) => {
                        return <MaestroRelationship type={object.owner_type == 'App\\Models\\Business' ? 'business' : 'contact'} id={object.owner_id} community={object.community.subdomain}>{object.owner.name}</MaestroRelationship>
                    }
                },
                property_units: {
                    label: '# of Units',
                    transformer: (object) => {
                        return (object.property_units.length);
                    }
                },
            },
        }
    },
    event: {
        endpoint: 'events',
        singularLabel: 'Event',
        pluralLabel: 'Events',
        fields: {
            primary: 'name',
            photo: null,
            additional: null,
        }
    },
    donation: {
        endpoint: 'donations',
        endpointInclude: 'all',
        singularLabel: 'Donation',
        pluralLabel: 'Donations',
        fields: {
            primary: (object) => {
                if(object.donor) {
                    return `\$${object.amount} from ${object.donor.name}`;
                }
                return `\$${object.amount}`;
            },
            photo: null,
            additional: {
                donor: {
                    label: 'Donor',
                    transformer: (object) => {
                        return <MaestroRelationship type={object.donor_type == 'App\\Models\\Business' ? 'business' : 'contact'} id={object.donor_id} community={object.community.subdomain}>{object.donor?.name ?? 'N/A'}</MaestroRelationship>
                    }
                },
                campaign: {
                    label: 'Campaign',
                    transformer: (object) => {
                        return object.campaign.name
                    }
                },
                fund: {
                    label: 'Fund',
                    transformer: (object) => {
                        return object.fund.name
                    }
                },
                appeal: {
                    label: 'Appeal',
                    transformer: (object) => {
                        return object.appeal.name
                    }
                },
            }
        }
    },
    importer: {
        endpoint: 'importers',
        singularLabel: 'Importer',
        pluralLabel: 'Importers',
        fields: {
            primary: 'name',
            photo: null,
            additional: null,
        }
    },
    opportunity: {
        endpoint: 'opportunities',
        singularLabel: 'Opportunity',
        pluralLabel: 'Opportunities',
        fields: {
            primary: 'name',
            photo: null,
            additional: null,
        }
    },
};

// alternate spellings
objectTypes.contacts = objectTypes.contact;
objectTypes.workplans = objectTypes.workplan;
objectTypes['work-plan'] = objectTypes.workplan;
objectTypes['work-plans'] = objectTypes.workplan;
objectTypes.businesses = objectTypes.business;
objectTypes.properties = objectTypes.property;
objectTypes.events = objectTypes.event;
objectTypes.donations = objectTypes.donation;
objectTypes.importers = objectTypes.importer;
objectTypes.opportunities = objectTypes.opportunity;

const MaestroRelationship = memo(props => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [object, setObject] = useState(null);

    const basicObject = <span>{props.children}</span>;

    // return basicObject;

    if(props.type === null || props.id === null)
    {
        console.warn('MaestroRelationship missing required property');
        console.warn(props);
        return basicObject;
    }

    if(!objectTypes.hasOwnProperty(props.type)) {
        console.warn('MaestroRelationship invalid type provided. Must be one of: ', objectTypes);
        return basicObject;
    }

    // used for preloading
    if(object === null && props.object !== null) {
        setObject(props.object);
    }

    const loadObjectDetails = (event) => {
        if(isLoading || object !== null) return;

        let requestUrl = `${objectTypes[props.type].endpoint}/${props.id}`;

        if(objectTypes[props.type].hasOwnProperty('endpointInclude')) {
            requestUrl = `${requestUrl}?include=${objectTypes[props.type].endpointInclude}`;
        }

        setIsLoading(true);
        authenticatedApi.get(requestUrl)
            .then(response => {
                setIsLoaded(true);
                setIsLoading(false);
                setObject(response);
            }).catch((e) => {
                setIsLoaded(true);
                setIsLoading(false);
                setError(e);
                console.warn(e);
            });
    }

    let popupDetails = null;

    let relationshipUrl;
    if(props.community) {
        relationshipUrl = communityUrl.replace('%subdomain%', props.community);
    } else {
        relationshipUrl = baseUrl;
    }

    let endpoint = objectTypes[props.type].endpoint;
    if(endpoint === 'workplans') {
        endpoint = 'work-plans';
    }

    return <a href={`${relationshipUrl}/${endpoint}/${props.id}/`} target="_blank" className={"clickable cursor-pointer"}>{props.children}</a>;
});

MaestroRelationship.propTypes = {
    type: PropTypes.oneOf(Object.keys(objectTypes)).isRequired,
    id: PropTypes.number.isRequired,
    disabled: PropTypes.bool,
    object: PropTypes.object,
};

MaestroRelationship.defaultProps = {
    type: null,
    id: null,
    disabled: false,
    object: null,
};


// const MemoMaestroRelationship = memo(props => <MaestroRelationship {...props} />);

export default MaestroRelationship;
